import { useState } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { 
  Mail, 
  Compass,
  Flame,
  Target,
  Sparkles,
  Mountain,
  Sun,
  Zap,
  Lock
} from "lucide-react";

// Category icons and colors
const CATEGORY_CONFIG = {
  Identity: { icon: Sun, color: "bg-amber-500/20 text-amber-600", borderColor: "border-amber-500/30" },
  Growth: { icon: Sparkles, color: "bg-emerald-500/20 text-emerald-600", borderColor: "border-emerald-500/30" },
  Discipline: { icon: Target, color: "bg-blue-500/20 text-blue-600", borderColor: "border-blue-500/30" },
  Mastery: { icon: Mountain, color: "bg-purple-500/20 text-purple-600", borderColor: "border-purple-500/30" },
  Purpose: { icon: Compass, color: "bg-rose-500/20 text-rose-600", borderColor: "border-rose-500/30" },
  Transformation: { icon: Flame, color: "bg-orange-500/20 text-orange-600", borderColor: "border-orange-500/30" },
  default: { icon: Zap, color: "bg-gray-500/20 text-gray-600", borderColor: "border-gray-500/30" }
};

// Locked state styling
const LOCKED_STYLE = {
  borderColor: "border-gray-500/30",
  overlay: "bg-black/40"
};

export function QuestCard({ 
  quest, 
  source = "discover",
  variant = "default", // default, compact, featured
  sessionId = null,
  onClickTracked = null
}) {
  const { token } = useAuth();
  const [isTracking, setIsTracking] = useState(false);
  
  const isLocked = quest.is_locked || quest.status === "locked";
  const categoryConfig = CATEGORY_CONFIG[quest.category] || CATEGORY_CONFIG.default;
  const CategoryIcon = categoryConfig.icon;

  // Generate mailto link for waitlist
  const getWaitlistMailto = () => {
    const questName = quest.title || "Quest";
    const subject = encodeURIComponent(`Waitlist: ${questName}`);
    const body = encodeURIComponent(`Hi, I'd like to be added to the waitlist for the ${questName}.`);
    return `mailto:info@truenorth-zone.com?subject=${subject}&body=${body}`;
  };

  const handleQuestClick = async () => {
    // Don't allow click if locked
    if (isLocked) return;
    if (isTracking) return;
    
    setIsTracking(true);
    
    try {
      // Track the click
      await axios.post(
        `${API}/quests/${quest.quest_id}/click`,
        { source, session_id: sessionId },
        { withCredentials: true }
      );
      
      if (onClickTracked) {
        onClickTracked(quest.quest_id);
      }
    } catch (err) {
      console.error("Failed to track quest click:", err);
    } finally {
      setIsTracking(false);
    }
    
    // Open waitlist mailto
    window.location.href = getWaitlistMailto();
  };

  if (variant === "compact") {
    return (
      <Card 
        className={`glass-card overflow-hidden ${isLocked ? 'opacity-70' : 'cursor-pointer group hover:scale-[1.02]'} transition-all border ${isLocked ? LOCKED_STYLE.borderColor : categoryConfig.borderColor}`}
        onClick={isLocked ? undefined : handleQuestClick}
        data-testid={`quest-card-${quest.quest_id}`}
      >
        <CardContent className="p-4 flex items-center gap-4 relative">
          {isLocked && (
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-10">
              <Badge className="bg-gray-700 text-white/80">
                <Lock className="w-3 h-3 mr-1" />
                {quest.lock_message || "Locked"}
              </Badge>
            </div>
          )}
          <div className={`w-12 h-12 rounded-xl ${categoryConfig.color} flex items-center justify-center shrink-0 ${isLocked ? 'opacity-50' : ''}`}>
            <CategoryIcon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-heading text-foreground truncate ${isLocked ? 'opacity-60' : ''}`}>{quest.title}</h3>
            <p className="text-xs text-foreground/50">{quest.category}</p>
          </div>
          {!isLocked && <Mail className="w-4 h-4 text-foreground/40 group-hover:text-primary transition-colors" />}
        </CardContent>
      </Card>
    );
  }

  if (variant === "featured") {
    return (
      <Card 
        className={`glass-card overflow-hidden ${isLocked ? 'opacity-80' : 'cursor-pointer group hover:scale-[1.01]'} transition-all border-2 ${isLocked ? LOCKED_STYLE.borderColor : categoryConfig.borderColor}`}
        onClick={isLocked ? undefined : handleQuestClick}
        data-testid={`quest-card-featured-${quest.quest_id}`}
      >
        <CardContent className="p-0 relative">
          {quest.thumbnail && (
            <div className="h-32 relative overflow-hidden">
              <img 
                src={quest.thumbnail} 
                alt={quest.title}
                className={`w-full h-full object-cover ${isLocked ? '' : 'group-hover:scale-105'} transition-transform duration-500 ${isLocked ? 'grayscale opacity-70' : ''}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              {isLocked && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <Badge className="bg-gray-800/90 text-white border border-white/20 px-4 py-2">
                    <Lock className="w-4 h-4 mr-2" />
                    {quest.lock_message || "Locked"}
                  </Badge>
                </div>
              )}
              <Badge className={`absolute top-3 left-3 ${categoryConfig.color}`}>
                <CategoryIcon className="w-3 h-3 mr-1" />
                {quest.category}
              </Badge>
            </div>
          )}
          <div className="p-5">
            {!quest.thumbnail && (
              <Badge className={`mb-3 ${categoryConfig.color}`}>
                <CategoryIcon className="w-3 h-3 mr-1" />
                {quest.category}
              </Badge>
            )}
            <h3 className={`font-heading text-lg text-foreground mb-2 ${isLocked ? 'opacity-70' : ''}`}>{quest.title}</h3>
            <p className={`text-sm text-foreground/60 mb-4 line-clamp-2 ${isLocked ? 'opacity-60' : ''}`}>{quest.description}</p>
            <Button 
              className={`w-full ${isLocked ? 'bg-gray-600 hover:bg-gray-600 cursor-not-allowed' : 'btn-primary group-hover:shadow-lg'} transition-shadow`}
              disabled={isTracking || isLocked}
            >
              {isLocked ? (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  {quest.lock_message || "Locked"}
                </>
              ) : (
                <>
                  Join the Waitlist
                  <Mail className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            {!isLocked && (
              <p className="text-foreground/40 text-xs text-center mt-2">
                Be first to know when {quest.title} launches.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Default variant
  return (
    <Card 
      className={`glass-card overflow-hidden ${isLocked ? 'opacity-80' : 'cursor-pointer group hover:scale-[1.01]'} transition-all border ${isLocked ? LOCKED_STYLE.borderColor : categoryConfig.borderColor}`}
      onClick={isLocked ? undefined : handleQuestClick}
      data-testid={`quest-card-${quest.quest_id}`}
    >
      <CardContent className="p-0 relative">
        <div className="flex">
          {quest.thumbnail ? (
            <div className="w-28 h-28 relative shrink-0 overflow-hidden">
              <img 
                src={quest.thumbnail} 
                alt={quest.title}
                className={`w-full h-full object-cover ${isLocked ? '' : 'group-hover:scale-110'} transition-transform duration-500 ${isLocked ? 'grayscale opacity-70' : ''}`}
              />
              {isLocked && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-white/80" />
                </div>
              )}
            </div>
          ) : (
            <div className={`w-28 h-28 shrink-0 ${categoryConfig.color} flex items-center justify-center ${isLocked ? 'opacity-50' : ''}`}>
              {isLocked ? <Lock className="w-10 h-10" /> : <CategoryIcon className="w-10 h-10" />}
            </div>
          )}
          <div className="flex-1 p-4 bg-[#f5f9f5]">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`text-xs ${categoryConfig.color}`}>
                {quest.category}
              </Badge>
              {isLocked && (
                <Badge className="bg-gray-500/20 text-gray-600 text-xs">
                  <Lock className="w-3 h-3 mr-1" />
                  {quest.lock_message || "Locked"}
                </Badge>
              )}
            </div>
            <h3 className={`text-[#a69373] font-heading text-base mb-1 line-clamp-1 ${isLocked ? 'opacity-60' : ''}`}>{quest.title}</h3>
            <p className={`text-[#a69373]/60 text-sm line-clamp-2 mb-2 ${isLocked ? 'opacity-50' : ''}`}>{quest.description}</p>
            {!isLocked && (
              <div className="flex items-center text-primary text-sm font-medium">
                Join the Waitlist
                <Mail className="w-3 h-3 ml-1" />
              </div>
            )}
            {!isLocked && (
              <p className="text-foreground/30 text-xs mt-1">
                Be first to know when {quest.title} launches.
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default QuestCard;
